/**
 * Staff Console > MCP tab (2026-10-03): "Use DCA in Claude" operations.
 *
 *   - Global write switch (KV mcp:writes:enabled), with a confirm step.
 *   - Every connection across users (label, mode, last used, expiry, revoke).
 *   - Tool-call activity (mcp_activity, written by the MCP worker) with
 *     filters, per-user 24h/7d counts and error rate.
 * QA (read-only) sees everything but the switch and revoke are disabled;
 * the leads worker refuses their non-GET calls anyway.
 */

import { useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { Bot, Loader2, RefreshCw, ShieldAlert, ShieldCheck, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { fmtLocale } from '@/lib/dates'
import { isStaffUser } from './staff'
import {
  useAdminMcpActivity,
  useAdminMcpConnections,
  useAdminRevokeMcp,
  useSetMcpWrites,
  type AdminMcpActivityFilters,
} from './use-admin'

const pct = (n: number) => `${(n * 100).toFixed(1)}%`

function WriteSwitchCard({ enabled, canEdit }: { readonly enabled: boolean; readonly canEdit: boolean }) {
  const setWrites = useSetMcpWrites()
  const [confirming, setConfirming] = useState(false)
  const next = !enabled
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base text-[var(--color-silver-bright)]">
          {enabled ? <ShieldAlert className="h-4 w-4 text-[#FBBF24]" /> : <ShieldCheck className="h-4 w-4 text-[#34D399]" />}
          Claude sending (global write switch)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-[var(--color-silver)]">
          {enabled
            ? 'ON: connections with "can send" may also send, approve drafts, book meetings and resume campaigns.'
            : 'OFF: nothing reaches a lead through Claude. Connections with "can send" can still write drafts to the review queue, reject drafts, hand campaigns to Claude, record lead outcomes and pause campaigns.'}
        </p>
        {canEdit ? (
          confirming ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-[var(--color-silver-bright)]">
                {next ? 'Turn Claude sending ON for every user with a "can send" connection?' : 'Turn Claude sending OFF for everyone?'}
              </span>
              <Button
                size="sm"
                variant={next ? 'destructive' : 'default'}
                disabled={setWrites.isPending}
                onClick={() => setWrites.mutate(next, { onSuccess: () => setConfirming(false) })}
              >
                {setWrites.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                Yes, turn {next ? 'on' : 'off'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Cancel</Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setConfirming(true)}>
              Turn sending {next ? 'on' : 'off'}
            </Button>
          )
        ) : (
          <p className="text-xs text-amber-300">Read-only access: the switch is disabled.</p>
        )}
        {setWrites.error instanceof Error && <p className="text-xs text-destructive">{setWrites.error.message}</p>}
      </CardContent>
    </Card>
  )
}

function ConnectionsCard({ canEdit }: { readonly canEdit: boolean }) {
  const { data, isLoading } = useAdminMcpConnections()
  const revoke = useAdminRevokeMcp()
  const [confirmId, setConfirmId] = useState<string | null>(null)
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base text-[var(--color-silver-bright)]">
          <Bot className="h-4 w-4 text-[#8FB4FA]" />
          Connections
          <span className="text-xs font-normal text-[var(--color-silver)]">({data?.connections.length ?? 0})</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-[var(--color-silver)]" />
        ) : !data?.connections.length ? (
          <p className="text-sm text-[var(--color-silver)]">No MCP connections yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="text-right">Revoke</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.connections.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="text-xs">
                      <div className="text-[var(--color-silver-bright)]">{c.email ?? c.user_id}</div>
                      <div className="font-mono text-[10px] text-[var(--color-silver)]">{c.prefix}</div>
                    </TableCell>
                    <TableCell className="text-xs">{c.label || '(no label)'}</TableCell>
                    <TableCell>
                      <Badge
                        className={cn(
                          'text-[10px]',
                          c.expired
                            ? 'bg-[rgba(248,113,113,0.12)] text-[#F87171]'
                            : c.write
                              ? 'bg-[rgba(251,191,36,0.12)] text-[#FBBF24]'
                              : 'bg-[rgba(16,185,129,0.12)] text-[#34D399]',
                        )}
                      >
                        {c.expired ? 'expired' : c.write ? (c.skip_qg ? 'can send, skips QG' : 'can send') : 'read only'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{c.last_used_at ? fmtLocale(c.last_used_at) : 'never'}</TableCell>
                    <TableCell className="text-xs">{c.expires_at ? fmtLocale(c.expires_at, 'date') : 'no expiry (legacy)'}</TableCell>
                    <TableCell className="text-right">
                      {!canEdit ? null : confirmId === c.id ? (
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-7 text-xs"
                            disabled={revoke.isPending}
                            onClick={() => revoke.mutate(c.id, { onSuccess: () => setConfirmId(null) })}
                          >
                            Confirm
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setConfirmId(null)}>Cancel</Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={() => setConfirmId(c.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {revoke.error instanceof Error && <p className="mt-2 text-xs text-destructive">{revoke.error.message}</p>}
      </CardContent>
    </Card>
  )
}

function ActivityCard() {
  const [filters, setFilters] = useState<AdminMcpActivityFilters>({ hours: 24, ok: '' })
  const [userInput, setUserInput] = useState('')
  const [toolInput, setToolInput] = useState('')
  const { data, isLoading, refetch, isRefetching } = useAdminMcpActivity(filters)

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between gap-2 text-base text-[var(--color-silver-bright)]">
          <span>Tool calls</span>
          <Button size="sm" variant="ghost" onClick={() => void refetch()} disabled={isRefetching}>
            <RefreshCw className={cn('h-3.5 w-3.5', isRefetching && 'animate-spin')} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {data?.table_missing && (
          <p className="rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-xs text-amber-300">
            The mcp_activity table does not exist yet (migration 0050 ships with the MCP worker). Nothing to show.
          </p>
        )}
        {data?.query_failed && <p className="text-xs text-destructive">The activity query failed. Check the leads worker logs.</p>}

        {data && (
          <div className="flex flex-wrap gap-3">
            {[
              { label: 'Calls 24h', value: data.totals.calls_24h.toLocaleString() },
              { label: 'Errors 24h', value: `${data.totals.errors_24h} (${pct(data.totals.error_rate_24h)})` },
              { label: 'Calls 7d', value: data.totals.calls_7d.toLocaleString() },
              { label: 'Errors 7d', value: `${data.totals.errors_7d} (${pct(data.totals.error_rate_7d)})` },
            ].map((s) => (
              <div key={s.label} className="min-w-[140px] flex-1 rounded-lg border border-border p-3">
                <p className="eyebrow text-[var(--color-silver)]">{s.label}</p>
                <p className="mt-1 text-xl font-bold tabular-nums text-[var(--color-silver-bright)]">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {data && data.per_user.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead className="text-right">Calls 24h</TableHead>
                  <TableHead className="text-right">Calls 7d</TableHead>
                  <TableHead className="text-right">Errors 7d</TableHead>
                  <TableHead className="text-right">Error rate 7d</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.per_user.map((u) => (
                  <TableRow
                    key={u.user_id}
                    className="cursor-pointer"
                    onClick={() => {
                      setUserInput(u.user_id)
                      setFilters((f) => ({ ...f, user_id: u.user_id }))
                    }}
                  >
                    <TableCell className="text-xs">{u.email ?? u.user_id}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{u.calls_24h}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{u.calls_7d}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{u.errors_7d}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{pct(u.error_rate_7d)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            setFilters((f) => ({ ...f, user_id: userInput.trim() || undefined, tool: toolInput.trim() || undefined }))
          }}
        >
          <Input className="h-8 w-56 text-xs" placeholder="user_id" value={userInput} onChange={(e) => setUserInput(e.target.value)} />
          <Input className="h-8 w-44 text-xs" placeholder="tool (e.g. submit_message)" value={toolInput} onChange={(e) => setToolInput(e.target.value)} />
          <select
            className="h-8 rounded-md border bg-transparent px-2 text-xs"
            value={filters.ok ?? ''}
            onChange={(e) => setFilters((f) => ({ ...f, ok: e.target.value as AdminMcpActivityFilters['ok'] }))}
          >
            <option value="">All results</option>
            <option value="1">OK only</option>
            <option value="0">Errors only</option>
          </select>
          <select
            className="h-8 rounded-md border bg-transparent px-2 text-xs"
            value={filters.hours ?? 24}
            onChange={(e) => setFilters((f) => ({ ...f, hours: Number(e.target.value) }))}
          >
            <option value={1}>Last hour</option>
            <option value={24}>Last 24h</option>
            <option value={168}>Last 7 days</option>
            <option value={720}>Last 30 days</option>
          </select>
          <Button type="submit" size="sm" variant="outline" className="h-8">Apply</Button>
          {(filters.user_id || filters.tool) && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8"
              onClick={() => {
                setUserInput('')
                setToolInput('')
                setFilters((f) => ({ ...f, user_id: undefined, tool: undefined }))
              }}
            >
              Clear
            </Button>
          )}
        </form>

        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-[var(--color-silver)]" />
        ) : !data?.rows.length ? (
          <p className="text-sm text-[var(--color-silver)]">No tool calls match.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Tool</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Lead</TableHead>
                  <TableHead className="text-right">ms</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-xs">{fmtLocale(r.created_at)}</TableCell>
                    <TableCell className="text-xs">
                      {r.email ?? r.user_id}
                      {r.token_prefix && <span className="ml-1 font-mono text-[10px] text-[var(--color-silver)]">{r.token_prefix}</span>}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{r.tool}</TableCell>
                    <TableCell className="text-xs">
                      {r.ok ? (
                        <span className="text-[#34D399]">ok</span>
                      ) : (
                        <span className="text-[#F87171]" title={r.error ?? undefined}>
                          error{r.error ? `: ${r.error.slice(0, 80)}` : ''}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-[10px]">{r.lead_id ?? ''}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{r.duration_ms ?? ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function McpPage() {
  const { user } = useUser()
  const canEdit = isStaffUser(user?.id)
  const { data } = useAdminMcpConnections()
  return (
    <div className="space-y-4">
      <WriteSwitchCard enabled={data?.writes_enabled ?? false} canEdit={canEdit && data !== undefined} />
      <ConnectionsCard canEdit={canEdit} />
      <ActivityCard />
    </div>
  )
}
